import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faFileWord,
  faGavel,
  faListCheck,
  faMicrophone,
  faPen,
  faPlay,
  faStop,
  faVideo,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import ConfirmModal from "components/ConfirmModal";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";

import councilPortalService from "api/councilPortalService";
import councilService from "api/councilService";
import pluginService from "api/pluginService";
import {
  ECouncilSessionState,
  ECouncilVotingResult,
  ICouncilAgendaItem,
  ICouncilSession,
} from "dto/Council/ICouncil";
import {
  ECouncilSpeakType,
  ISaveAnalogCouncilVotingRequest,
  IStartCouncilVotingRequest,
} from "dto/Council/ICouncilPortal";
import { EObsOutputAction, EObsOutputKind } from "dto/Plugin/councilStream";
import { ENotificationType, notification } from "utils/notification";

import ProtocolJournalModal from "./ProtocolJournalModal";
import StreamPanel from "./StreamPanel";
import VotingModal from "./VotingModal";
import useCouncilProtocol from "./useCouncilProtocol";
import useCouncilStream from "./useCouncilStream";
import { decodeAgendaItems, decodeSession, decodeText } from "./decodeText";
import styles from "./styles.module.scss";

// Biuro rady → „Prowadzenie obrad". Odwzorowanie sterowania sesją z Magistratu
// (AgendaViewModel: start/przerwa/koniec, obecność, wskazanie punktu) oraz
// VotingsWorkspaceViewModel (otwieranie i zamykanie głosowań).
//
// Panel odświeża się co 4 sekundy: radni meldują obecność i głosują z portalu,
// więc bez odpytywania prowadzący patrzyłby na stan sprzed ich akcji.
// W Magistracie tę rolę pełniło pchnięcie przez WCF; tu wystarczy krótki polling.
const REFRESH_MS = 4000;

// Stan OBS zmienia się poza aplikacją (realizator przełącza sceny w samym OBS),
// więc panel odpytuje wtyczkę częściej niż backend.
const STREAM_REFRESH_MS = 3000;

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const stateLabel = (state: ECouncilSessionState) => {
  switch (state) {
    case ECouncilSessionState.InProgress:
      return "obrady trwają";
    case ECouncilSessionState.Suspended:
      return "przerwa w obradach";
    case ECouncilSessionState.Finished:
      return "obrady zakończone";
    case ECouncilSessionState.Closed:
      return "sesja zamknięta";
    case ECouncilSessionState.PresenceCheck:
      return "sprawdzanie obecności";
    default:
      return "przed rozpoczęciem";
  }
};

const speakTypeLabel = (type: ECouncilSpeakType) => {
  if (type === ECouncilSpeakType.Formal) return "w kwestii proceduralnej";
  if (type === ECouncilSpeakType.AdVocem) return "ad vocem";
  return "zabranie głosu";
};

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "";

const ConductView = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [votingFor, setVotingFor] = useState<ICouncilAgendaItem | null>(null);
  const [confirmEnd, setConfirmEnd] = useState<"end" | "close" | "cancelVoting" | null>(
    null,
  );
  const [journalFor, setJournalFor] = useState<{ councilorId: string | null } | null>(
    null,
  );

  const sessionsQuery = councilService.useGetSessions({ archived: false });

  const sessions = useMemo(
    () =>
      (sessionsQuery.data?.data ?? [])
        .filter((s) => s.State < ECouncilSessionState.Closed && s.WasPublished)
        .map(decodeSession),
    [sessionsQuery.data],
  );

  // Domyślnie wchodzimy na sesję, która faktycznie trwa; dopiero potem na opublikowaną.
  useEffect(() => {
    if (!sessions.length) {
      setSelectedId(null);
      return;
    }

    if (selectedId && sessions.some((s) => s.Id === selectedId)) return;

    const running = sessions.find(
      (s) =>
        s.State === ECouncilSessionState.InProgress ||
        s.State === ECouncilSessionState.Suspended,
    );

    setSelectedId((running ?? sessions.find((s) => s.IsActive) ?? sessions[0]).Id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessions]);

  const conductQuery = councilPortalService.useGetConduct(
    selectedId ?? undefined,
    REFRESH_MS,
  );
  const agendaQuery = councilService.useGetAgenda(selectedId ?? undefined);
  const votingsQuery = councilPortalService.useGetVotings(
    selectedId ?? undefined,
    REFRESH_MS,
  );

  const { mutate: sessionAction, isLoading: changingState } =
    councilPortalService.useSessionAction();
  const { mutate: setActiveItem } = councilPortalService.useSetActiveAgendaItem();
  const { mutate: setPresence } = councilPortalService.useSetPresence();
  const { mutate: letSpeak } = councilPortalService.useLetSpeak();
  const { mutate: startVoting, isLoading: startingVoting } =
    councilPortalService.useStartVoting();
  const { mutate: saveAnalogVoting, isLoading: savingAnalog } =
    councilPortalService.useSaveAnalogVoting();
  const { mutate: endVoting, isLoading: endingVoting } =
    councilPortalService.useEndVoting();
  const { mutate: cancelVoting } = councilPortalService.useCancelVoting();

  // ── Transmisja obrad ─────────────────────────────────────────────────────
  // Ustawienia mówią, czy urząd w ogóle korzysta z transmisji i z kamer; bez tego
  // nie zaczepiamy wtyczki, żeby panel nie pokazywał podpowiedzi o niej urzędom,
  // które transmisji nie prowadzą.
  const settingsQuery = councilPortalService.useGetPortalSettings();
  const { mutate: savePortalSettings, isLoading: savingSceneRole } =
    councilPortalService.useSavePortalSettings();

  const portalSettings = settingsQuery.data?.data;
  const streamConfigured =
    !!portalSettings?.SupportsStreaming || !!portalSettings?.EnableDahuaIntegration;

  const streamQuery = pluginService.useCouncilStreamState(
    streamConfigured,
    STREAM_REFRESH_MS,
  );

  const streamState = streamQuery.data;

  // Dopiero brak JAKIEGOKOLWIEK stanu znaczy „nie ma wtyczki". Zapytanie leci co
  // 3 sekundy z limitem 6 s, a wtyczka bywa chwilowo zajęta (łączenie z OBS, skaner) —
  // bez tego warunku jedno spóźnione odpytanie zabierało prowadzącemu w trakcie
  // transmisji cały panel razem z przyciskiem „Zakończ transmisję".
  const pluginMissing = streamConfigured && streamQuery.isError && !streamState;

  const stream = useCouncilStream({
    state: streamState,
    refresh: () => streamQuery.refetch(),
  });

  // ── Protokół ─────────────────────────────────────────────────────────────
  const protocolQuery = councilPortalService.useGetProtocolDocument(
    selectedId ?? undefined,
  );
  const protocolRef = protocolQuery.data?.data;
  const protocol = useCouncilProtocol();

  const conduct = conductQuery.data?.data;

  // Backend koduje HTML-owo każdy ciąg znaków, a tytuł sesji idzie stąd wprost na
  // napisy transmisji — bez odkodowania widz zobaczyłby encje zamiast cudzysłowów.
  const session: ICouncilSession | null = conduct?.Session
    ? decodeSession(conduct.Session)
    : null;

  const presenceOf = (councilorId: string) =>
    conduct?.Presence.find((p) => p.CouncilorId === councilorId);

  /**
   * Rola sceny (domyślna / głosowania) jest ustawieniem urzędu, ale wskazuje się ją
   * tutaj, bo dopiero po połączeniu z OBS widać listę nazw scen. Zapis nie rusza
   * kamer ani haseł — pominięte pola oznaczają „bez zmiany".
   */
  const setSceneRole = (role: "default" | "voting", name: string) => {
    if (!portalSettings?.Slug) return;

    savePortalSettings(
      {
        Slug: portalSettings.Slug,
        // Każde pole tekstowe musi wrócić odkodowane — inaczej backend zakodowałby
        // je po raz drugi i przy kolejnych kliknięciach nazwa urzędu obrastałaby
        // warstwami encji. Formularz ustawień robi dokładnie to samo.
        OfficeName: decodeText(portalSettings.OfficeName) || null,
        PortalHeader: decodeText(portalSettings.PortalHeader) || null,
        TotalCouncilorsCount: portalSettings.TotalCouncilorsCount,
        AbsoluteVotingType: portalSettings.AbsoluteVotingType,
        SupportsStreaming: portalSettings.SupportsStreaming,
        YouTubeChannel: decodeText(portalSettings.YouTubeChannel) || null,
        ObsTextFilePath: decodeText(portalSettings.ObsTextFilePath) || null,
        ObsUseLocal: portalSettings.ObsUseLocal,
        ObsAddress: decodeText(portalSettings.ObsAddress) || null,
        ObsDefaultScene:
          role === "default"
            ? name || null
            : decodeText(portalSettings.ObsDefaultScene) || null,
        ObsVotingScene:
          role === "voting"
            ? name || null
            : decodeText(portalSettings.ObsVotingScene) || null,
        EnableDahuaIntegration: portalSettings.EnableDahuaIntegration,
        IsEnabled: portalSettings.IsEnabled,
      },
      {
        onSuccess: async () => {
          await settingsQuery.refetch();
          // Wtyczka trzyma konfigurację w pamięci — bez przeładowania scena
          // głosowania działałaby dopiero po kilku minutach.
          await pluginService.reloadCouncilStream().catch(() => undefined);
          streamQuery.refetch();
        },
        onError: fail("Nie udało się zapisać roli sceny"),
      },
    );
  };

  const items = useMemo(
    () => decodeAgendaItems(agendaQuery.data?.data ?? []),
    [agendaQuery.data],
  );

  const votings = votingsQuery.data?.data ?? [];
  const activeVoting = conduct?.ActiveVoting ?? null;

  const refreshAll = () => {
    conductQuery.refetch();
    votingsQuery.refetch();
    agendaQuery.refetch();
    sessionsQuery.refetch();
  };

  const fail = (title: string) => (e: unknown) =>
    notification({
      type: ENotificationType.ERROR,
      title,
      text: errorText(e, "Operacja się nie powiodła."),
    });

  const doAction = (action: "start" | "suspend" | "resume" | "end" | "close") => {
    if (!selectedId) return;
    setConfirmEnd(null);

    sessionAction(
      { sessionId: selectedId, action },
      {
        onSuccess: () => {
          refreshAll();

          // Transmisja idzie za stanem sesji: start uruchamia nadawanie, koniec je
          // zatrzymuje i gasi napisy. To odwzorowanie StartSession / EndSessionExecuted
          // z Magistratu; „close" to domknięcie protokolarne, transmisji nie dotyczy.
          if (action === "start") void stream.onSessionStarted();
          else if (action === "suspend") stream.onSessionSuspended();
          else if (action === "resume") stream.onSessionResumed();
          else if (action === "end") void stream.onSessionEnded();
        },
        onError: fail("Nie udało się zmienić stanu sesji"),
      },
    );
  };

  const doStartVoting = (request: IStartCouncilVotingRequest) => {
    if (!selectedId) return;

    startVoting(
      { sessionId: selectedId, request },
      {
        onSuccess: (result) => {
          setVotingFor(null);
          refreshAll();
          void stream.onVotingStarted(result.data.Description);
        },
        onError: fail("Nie udało się otworzyć głosowania"),
      },
    );
  };

  const doSaveAnalog = (request: ISaveAnalogCouncilVotingRequest) => {
    if (!selectedId) return;

    saveAnalogVoting(
      { sessionId: selectedId, request },
      {
        onSuccess: () => {
          setVotingFor(null);
          refreshAll();
        },
        onError: fail("Nie udało się zapisać wyniku"),
      },
    );
  };

  const doEndVoting = () => {
    if (!activeVoting) return;

    endVoting(activeVoting.Id, {
      onSuccess: (result) => {
        refreshAll();
        void stream.onVotingEnded(result.data);
        notification({
          type: ENotificationType.SUCCESS,
          title: "Głosowanie zakończone",
          text:
            result.data.Result === ECouncilVotingResult.Positive
              ? "Wniosek został przyjęty."
              : `Wynik: ${result.data.ResultLabel ?? "brak rozstrzygnięcia"}.`,
        });
      },
      onError: fail("Nie udało się zamknąć głosowania"),
    });
  };

  const doCancelVoting = () => {
    setConfirmEnd(null);
    if (!activeVoting) return;

    cancelVoting(activeVoting.Id, {
      onSuccess: () => {
        refreshAll();
        void stream.onVotingCanceled();
      },
      onError: fail("Nie udało się przerwać głosowania"),
    });
  };

  // Tytuł sesji i komunikat oczekiwania trafiają na transmisję po wejściu do panelu —
  // odpowiednik OnLoaded z Magistratu. Raz na sesję, żeby cykliczne odpytywanie
  // nie nadpisywało paska co kilka sekund.
  const announcedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!session || !stream.osdEnabled) return;
    if (announcedRef.current === session.Id) return;

    announcedRef.current = session.Id;
    stream.onPanelOpened(session);
  }, [session, stream]);

  if (sessionsQuery.isLoading) {
    return <Loading />;
  }

  if (!sessions.length) {
    return (
      <div className={styles.noSession}>
        <div className={styles.noSessionTitle}>Brak sesji do prowadzenia</div>
        <span className={styles.fieldLabel}>
          Prowadzić można sesję, której porządek obrad został opublikowany — bez tego
          radni nie zobaczą jej w portalu.
        </span>
      </div>
    );
  }

  const running = session?.State === ECouncilSessionState.InProgress;
  const suspended = session?.State === ECouncilSessionState.Suspended;
  const notStarted = session?.State === ECouncilSessionState.NotStarted;
  const finished =
    session?.State === ECouncilSessionState.Finished ||
    session?.State === ECouncilSessionState.Closed;

  return (
    <div className={styles.panel}>
      <div className={styles.topBar}>
        <select
          className={styles.textInput}
          value={selectedId ?? ""}
          onChange={(e) => setSelectedId(e.target.value)}
        >
          {sessions.map((s) => (
            <option key={s.Id} value={s.Id}>
              {formatDate(s.Date)} — {s.Number}
            </option>
          ))}
        </select>

        {session && (
          <span
            className={`${styles.publishedBadge} ${running ? styles.runningBadge : ""}`}
          >
            {stateLabel(session.State)}
          </span>
        )}

        <div className={styles.spacer} />

        {notStarted && (
          <Button
            text="Rozpocznij obrady"
            element={<FontAwesomeIcon icon={faPlay} />}
            loading={changingState}
            onClick={() => doAction("start")}
          />
        )}

        {running && (
          <>
            <HollowButton text="Ogłoś przerwę" onClick={() => doAction("suspend")} />
            <Button
              text="Zakończ obrady"
              color="error"
              element={<FontAwesomeIcon icon={faStop} />}
              loading={changingState}
              onClick={() => setConfirmEnd("end")}
            />
          </>
        )}

        {suspended && (
          <>
            <Button
              text="Wznów obrady"
              element={<FontAwesomeIcon icon={faPlay} />}
              loading={changingState}
              onClick={() => doAction("resume")}
            />
            <HollowButton text="Zakończ obrady" onClick={() => setConfirmEnd("end")} />
          </>
        )}

        {session?.State === ECouncilSessionState.Finished && (
          <HollowButton text="Zamknij sesję" onClick={() => setConfirmEnd("close")} />
        )}

        <button
          type="button"
          className={styles.conductAction}
          title="Przebieg sesji — wpisy, z których składany jest protokół"
          onClick={() => setJournalFor({ councilorId: null })}
        >
          <FontAwesomeIcon icon={faListCheck} />
          Przebieg
        </button>

        {/*
          Protokół powstaje RAZ dla zakończonej sesji, a potem jest już zwykłym
          dokumentem edytowalnym — tak samo jak w Magistracie, gdzie ustawione
          ProtocolId powodowało zwrócenie istniejącego dokumentu zamiast złożenia
          nowego. Przegenerowanie skasowałoby redakcję protokolanta.
        */}
        {finished && !!protocolRef && (
          <button
            type="button"
            className={`${styles.conductAction} ${styles.conductActionPrimary}`}
            disabled={
              protocol.busy || (!protocolRef.DocumentId && !protocolRef.CanGenerate)
            }
            title={protocolRef.DocumentId ? "Otwórz protokół w Wordzie" : (protocolRef.Reason ?? "Złóż protokół z przebiegu sesji")}
            onClick={() => {
              if (protocolRef.DocumentId) {
                protocol.open(protocolRef.DocumentId);
                return;
              }

              if (!session) return;

              protocol
                .generate(session)
                .then(() => {
                  protocolQuery.refetch();
                  notification({
                    type: ENotificationType.SUCCESS,
                    title: "Protokół złożony",
                    text: "Dokument otworzy się w Wordzie — dalsze zmiany zapisują się jak w każdym piśmie.",
                  });
                })
                .catch((e: Error) =>
                  notification({
                    type: ENotificationType.ERROR,
                    title: "Nie udało się złożyć protokołu",
                    text: e.message,
                  }),
                );
            }}
          >
            <FontAwesomeIcon icon={faFileWord} />
            {protocol.busy
              ? "Składanie…"
              : protocolRef.DocumentId
                ? "Otwórz protokół"
                : "Złóż protokół"}
          </button>
        )}
      </div>

      {conductQuery.isLoading && !conduct ? (
        <Loading />
      ) : (
        <div className={styles.conductWrap}>
          <div className={styles.conductMain}>
            {streamConfigured && (
              <StreamPanel
                state={streamState}
                pluginMissing={pluginMissing}
                onConnect={() => void stream.connect()}
                onDisconnect={() => void stream.disconnect()}
                onRunObs={() => void stream.runObs()}
                onSetOutput={(kind: EObsOutputKind, action: EObsOutputAction) =>
                  void stream.setOutput(kind, action)
                }
                onSelectScene={(name) => void stream.selectScene(name)}
                onSetSceneRole={setSceneRole}
                savingSceneRole={savingSceneRole}
                onGotoPreset={(cameraId, preset) => stream.gotoPreset(cameraId, preset)}
              />
            )}

            {activeVoting && (
              <div className={styles.activeVotingBox}>
                <div className={styles.activeVotingTitle}>
                  Trwa głosowanie: {activeVoting.Description}
                </div>
                <div className={styles.activeVotingMeta}>
                  Oddano głosów: {conduct?.Presence.filter((p) => p.HasVoted).length ?? 0}{" "}
                  z {conduct?.PresentCount ?? 0} obecnych
                  {activeVoting.IsClassified ? " · głosowanie tajne" : ""}
                </div>
                <div className={styles.activeVotingActions}>
                  <Button
                    text="Zamknij głosowanie"
                    element={<FontAwesomeIcon icon={faGavel} />}
                    loading={endingVoting}
                    onClick={doEndVoting}
                  />
                  <HollowButton
                    text="Przerwij"
                    onClick={() => setConfirmEnd("cancelVoting")}
                  />
                </div>
              </div>
            )}

            <div className={styles.conductSectionTitle}>Porządek obrad</div>

            {!running && !finished && (
              <div className={styles.conductHint}>
                {notStarted
                  ? "Rozpocznij obrady, aby otwierać głosowania nad punktami."
                  : "Wznów obrady, aby otwierać głosowania nad punktami."}
              </div>
            )}

            <div className={styles.agendaList}>
              {items.map((item) => {
                const itemVotings = votings.filter((v) => v.AgendaItemId === item.Id);
                const isActive = conduct?.ActiveAgendaItemId === item.Id;

                return (
                  <div
                    key={item.Id}
                    className={`${styles.conductItem} ${
                      isActive ? styles.conductItemActive : ""
                    }`}
                  >
                    <span className={styles.itemOrder}>{item.Order}</span>

                    <div className={styles.conductItemBody}>
                      <span className={styles.itemText}>{item.Title}</span>

                      {itemVotings.map((v) => (
                        <div key={v.Id} className={styles.votingResultRow}>
                          <span className={styles.votingResultLabel}>
                            {v.ResultLabel ?? "w toku"}
                          </span>
                          <span className={styles.votingResultMeta}>
                            {v.Description} · za {v.For}, przeciw {v.Against}, wstrzymało
                            się {v.Abstain}
                            {v.WithoutVote ? `, bez głosu ${v.WithoutVote}` : ""}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/*
                      Panel prowadzącego celowo NIE używa paska narzędzi z edytora
                      porządku obrad: tamten odsłania się dopiero po najechaniu na wiersz,
                      a tutaj są to główne czynności podczas obrad — muszą być widoczne
                      i podpisane, nie ukryte pod hoverem.
                    */}
                    <div className={styles.conductActions}>
                      <button
                        type="button"
                        className={styles.conductAction}
                        disabled={finished || isActive}
                        title={
                          isActive
                            ? "Ten punkt jest właśnie omawiany"
                            : "Oznacz jako omawiany — radni zobaczą go w portalu"
                        }
                        onClick={() =>
                          selectedId &&
                          setActiveItem(
                            { sessionId: selectedId, itemId: item.Id },
                            {
                              onSuccess: () => {
                                refreshAll();
                                stream.onAgendaItem(item.Order, item.Title);
                              },
                              onError: fail("Nie udało się wskazać punktu"),
                            },
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faMicrophone} />
                        {isActive ? "Omawiany" : "Omawiaj"}
                      </button>

                      <button
                        type="button"
                        className={styles.conductAction}
                        disabled={finished && session?.State === ECouncilSessionState.Closed}
                        title="Dopisz wystąpienie, wniosek albo interpelację przy tym punkcie"
                        onClick={() => {
                          if (selectedId)
                            setActiveItem(
                              { sessionId: selectedId, itemId: item.Id },
                              { onSuccess: refreshAll },
                            );

                          setJournalFor({ councilorId: null });
                        }}
                      >
                        <FontAwesomeIcon icon={faPen} />
                        Wpis
                      </button>

                      <button
                        type="button"
                        className={`${styles.conductAction} ${styles.conductActionPrimary}`}
                        disabled={!running || !!activeVoting}
                        title={
                          !running
                            ? "Głosowanie można otworzyć dopiero po rozpoczęciu obrad"
                            : activeVoting
                              ? "Najpierw zamknij trwające głosowanie"
                              : "Otwórz głosowanie nad tym punktem"
                        }
                        onClick={() => setVotingFor(item)}
                      >
                        <FontAwesomeIcon icon={faGavel} />
                        Głosowanie
                      </button>
                    </div>
                  </div>
                );
              })}

              {!items.length && (
                <div className={styles.emptyAgenda}>
                  Ta sesja nie ma jeszcze punktów porządku obrad.
                </div>
              )}
            </div>
          </div>

          <div className={styles.conductSide}>
            <div className={styles.conductSectionTitle}>
              Obecność {conduct?.PresentCount ?? 0}/{conduct?.TotalCouncilorsCount ?? 0}
              {conduct && (
                <span
                  className={
                    conduct.HasQuorum ? styles.quorumOk : styles.quorumMissing
                  }
                >
                  {conduct.HasQuorum ? "kworum" : "brak kworum"}
                </span>
              )}
            </div>

            <div className={styles.presenceList}>
              {(conduct?.Presence ?? []).map((p) => (
                <div key={p.CouncilorId} className={styles.presenceRow}>
                  {/*
                    Etykieta obejmuje wyłącznie pole wyboru i nazwisko — przycisk kamery
                    musi zostać poza nią, bo kliknięcie wewnątrz <label> przełączyłoby
                    przy okazji obecność radnego.
                  */}
                  <label className={styles.presenceMain}>
                    <input
                      type="checkbox"
                      checked={p.IsPresent}
                      disabled={finished}
                      onChange={(e) =>
                        selectedId &&
                        setPresence(
                          {
                            sessionId: selectedId,
                            councilorId: p.CouncilorId,
                            isPresent: e.target.checked,
                          },
                          {
                            onSuccess: refreshAll,
                            onError: fail("Nie udało się zapisać obecności"),
                          },
                        )
                      }
                    />
                    <span className={styles.presenceName}>{p.FullName}</span>
                  </label>

                  {activeVoting && p.IsPresent && (
                    <span
                      className={p.HasVoted ? styles.votedYes : styles.votedNo}
                      title={p.HasVoted ? "oddał głos" : "jeszcze nie głosował"}
                    >
                      <FontAwesomeIcon icon={p.HasVoted ? faCheck : faXmark} />
                    </span>
                  )}

                  {/*
                    Ręczne skierowanie kamery na radnego bez udzielania mu głosu —
                    odpowiednik SetCameraOnCounciliorCommand z Magistratu. Scena OBS
                    celowo się nie zmienia, tak jak tam.
                  */}
                  {stream.camerasEnabled && p.CameraPreset != null && (
                    <button
                      type="button"
                      className={styles.toolBtn}
                      title="Skieruj kamerę na radnego"
                      onClick={() => stream.onPointCameraAt(p)}
                    >
                      <FontAwesomeIcon icon={faVideo} />
                    </button>
                  )}
                </div>
              ))}

              {!conduct?.Presence.length && (
                <div className={styles.emptyAgenda}>
                  Brak radnych — dodaj ich w zakładce „Radni”.
                </div>
              )}
            </div>

            <div className={styles.conductSectionTitle}>Oczekujący na głos</div>

            <div className={styles.presenceList}>
              {(conduct?.SpeakQueue ?? []).map((q) => (
                <div key={q.Id} className={styles.speakRow}>
                  <div className={styles.conductItemBody}>
                    <span className={styles.presenceName}>{q.Label}</span>
                    <span className={styles.councilorMeta}>
                      {speakTypeLabel(q.Type)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Udziel głosu"
                    onClick={() =>
                      letSpeak(q.Id, {
                        onSuccess: () => {
                          refreshAll();
                          stream.onLetSpeak(presenceOf(q.CouncilorId));

                          // Samo udzielenie głosu nie jest jeszcze wystąpieniem —
                          // treść zna dopiero protokolant. Magistrat zostawiał ten
                          // wpis człowiekowi; tutaj otwieramy mu formularz z już
                          // wskazaną osobą.
                          setJournalFor({ councilorId: q.CouncilorId });
                        },
                        onError: fail("Nie udało się udzielić głosu"),
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faMicrophone} />
                  </button>
                </div>
              ))}

              {!conduct?.SpeakQueue.length && (
                <div className={styles.emptyAgenda}>Kolejka jest pusta.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {journalFor && selectedId && (
        <ProtocolJournalModal
          sessionId={selectedId}
          activeAgendaItemId={conduct?.ActiveAgendaItemId ?? null}
          agendaItems={items}
          readOnly={session?.State === ECouncilSessionState.Closed}
          defaultCouncilorId={journalFor.councilorId}
          onClose={() => setJournalFor(null)}
          onChanged={() => protocolQuery.refetch()}
        />
      )}

      {votingFor && (
        <VotingModal
          item={votingFor}
          loading={startingVoting || savingAnalog}
          onClose={() => setVotingFor(null)}
          onStart={doStartVoting}
          onSaveAnalog={doSaveAnalog}
        />
      )}

      {confirmEnd === "end" && (
        <ConfirmModal
          title="Zakończyć obrady?"
          text="Po zakończeniu nie będzie już można otwierać głosowań ani meldować obecności."
          acceptLabel="Zakończ"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={() => doAction("end")}
          onCancel={() => setConfirmEnd(null)}
        />
      )}

      {confirmEnd === "close" && (
        <ConfirmModal
          title="Zamknąć sesję?"
          text="Zamknięta sesja jest stanem końcowym — metryki nie da się już zmienić."
          acceptLabel="Zamknij sesję"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={() => doAction("close")}
          onCancel={() => setConfirmEnd(null)}
        />
      )}

      {confirmEnd === "cancelVoting" && (
        <ConfirmModal
          title="Przerwać głosowanie?"
          text="Głosowanie zostanie oznaczone jako anulowane. Oddane głosy pozostaną w metryce."
          acceptLabel="Przerwij"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={doCancelVoting}
          onCancel={() => setConfirmEnd(null)}
        />
      )}
    </div>
  );
};

export default ConductView;
